import '@nectary/components/button'
import '@nectary/components/icon'
import '@nectary/components/text'
import * as Sentry from '@sentry/react'
import QRCode from 'qrcode'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { V1Channels, V1MultiChannelWidget, V1Telegram, V1WhatsApp } from '../generated/widget'
import { trackEvent } from '../services/rudderanalytics.ts'

/** Channels supported by the forwarding page */
export type ForwardingChannel = 'whatsapp' | 'telegram' | 'facebook'

export type ForwardingPageContentProps = {
  /** Widget configuration data */
  widget: V1MultiChannelWidget
  /** Channel key (e.g. 'whatsapp', 'telegram') */
  channel: ForwardingChannel
  /** Optional close handler — if provided, renders a close button */
  onClose?: () => void
}

const Root = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: var(--sinch-sys-color-surface-primary-default, #fff);
`

const CloseButtonWrapper = styled.div`
  position: absolute;
  right: 16px;
  top: -16px;
  z-index: 2;
  --sinch-comp-button-shape-radius-size-xs: 8px;
  --sinch-comp-button-color-primary-default-background-initial: #f7f9fa;
  --sinch-comp-button-color-primary-default-background-hover: #e9eced;
  --sinch-comp-button-color-primary-default-background-active: #e0e3e4;
  --sinch-comp-button-color-primary-default-icon-initial: #1a2227;
  --sinch-comp-button-color-primary-default-icon-hover: #1b2227;
  --sinch-comp-button-color-primary-default-icon-active: #181f23;
  --sinch-comp-button-color-primary-default-border-initial: #dce2e5;
  --sinch-comp-button-color-primary-default-border-hover: #dce2e5;
  --sinch-comp-button-color-primary-default-border-active: #dce2e5;
  --sinch-comp-button-shadow-secondary-initial: 0px 0px 0px 0px;
`

const CenteredContentWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
`

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
  margin-top: 16px;
`

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 50%;
  margin-bottom: 8px;
`

const Logo = styled.img<{ $primaryColor?: string }>`
  display: flex;
  align-items: center;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  text-align: center;
  background-color: ${({ $primaryColor }) => ($primaryColor ? $primaryColor : '#ffbe3c')};
`

const QRCodeImg = styled.img<{ $apiError?: boolean }>`
  display: flex;
  align-items: center;
  align-self: center;
  width: 196px;
  height: 196px;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 24px;
  box-shadow: 0px 4px 16px rgba(26, 34, 39, 0.08);
  filter: ${({ $apiError }) => ($apiError ? 'blur(5px)' : 'blur(0)')};
`

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 390px;
  padding: 0 16px 16px 16px;
  gap: 24px;
`

const ActionButtonWrapper = styled.div`
  width: 100%;
`

const Separator = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: #626c73;
  width: 100%;

  &:before,
  &:after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #dce2e5;
  }
  &:not(:empty)::before {
    margin-right: 24px;
  }
  &:not(:empty)::after {
    margin-left: 24px;
  }
`

const fakeImg =
  'data:image/png;base64,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'

/**
 * ForwardingPageContent - Shared presentational component for the forwarding page.
 * Used by both the standalone forwarding-page service and the livechat widget panel.
 * Displays channel information, QR codes, and action buttons.
 */
export const ForwardingPageContent = ({ widget, channel, onClose }: ForwardingPageContentProps) => {
  const [imgDataUrl, setImgDataUrl] = useState('')
  const { t } = useTranslation('forwardingPage')

  const displayChannel = widget?.channels?.[channel as keyof V1Channels]

  // Resolve the human-readable channel name for translations
  const channelName = (displayChannel as V1WhatsApp | V1Telegram | undefined)?.name || t(`channelName.${channel}`)

  // Channel names come from external sources (e.g. a Telegram bot handle) and can be long enough to
  // overflow the title and the action button. Cap the displayed name with an ellipsis; the full name
  // stays in the aria-labels for accessibility.
  const MAX_DISPLAY_NAME = 24
  const displayName =
    channelName.length > MAX_DISPLAY_NAME ? `${channelName.slice(0, MAX_DISPLAY_NAME).trimEnd()}…` : channelName

  // Validate channel
  useEffect(() => {
    if (!displayChannel) {
      Sentry.captureMessage(`Forwarding page: channel "${channel}" not found in widget config`, {
        level: 'warning',
        extra: { channel, widgetId: widget?.id },
      })
      return
    }

    if (!(displayChannel as V1WhatsApp | V1Telegram | undefined)?.enabled) {
      Sentry.captureMessage(`Forwarding page: channel "${channel}" is disabled`, {
        level: 'warning',
        extra: { channel, widgetId: widget?.id },
      })
    }
  }, [channel, displayChannel, widget?.id])

  // TODO: add tracking via forwarding page service (tbd)
  // if implemented, we can track "engage_widget_messaging_channel_clicked" event as well
  const redirectLink =
    channel === 'whatsapp'
      ? `https://wa.me/${encodeURIComponent((displayChannel as V1WhatsApp)?.number || '')}?text=${encodeURIComponent((displayChannel as V1WhatsApp)?.startMessage || '')}`
      : channel === 'telegram'
        ? `https://t.me/${encodeURIComponent((displayChannel as V1Telegram)?.bot || '')}` // TODO: not working on mac when telegram is installed (it opens TG website)
        : null

  // Generate QR code
  useEffect(() => {
    if (redirectLink)
      QRCode.toDataURL(redirectLink)
        .then(r => {
          setImgDataUrl(r)
        })
        .catch(err => {
          Sentry.captureException(err, {
            extra: { channel, redirectLink },
          })
        })
    return () => setImgDataUrl('')
  }, [redirectLink, channel])

  return (
    <Root>
      {onClose && (
        <CloseButtonWrapper>
          <sinch-button aria-label={t('closeButton')} size={'xs'} type={'primary'} onClick={onClose}>
            <sinch-icon icons-version={'2'} name={'fa-minus'} slot={'icon'} />
          </sinch-button>
        </CloseButtonWrapper>
      )}
      <CenteredContentWrapper>
        <LogoContainer>
          {(displayChannel as V1WhatsApp | undefined)?.photoUrl && (
            <LogoWrapper>
              <Logo
                $primaryColor={widget?.settings?.primaryColor}
                src={(displayChannel as V1WhatsApp).photoUrl}
                alt={'Channel logo'}
              />
            </LogoWrapper>
          )}
          <sinch-title type='l' level={'2'} text={displayName} />
        </LogoContainer>
        <ContentSection>
          <sinch-title type='m' level={'3'} text={t('getInContact')} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <sinch-icon icons-version='2' name='fa-mobile' slot='icon' />
            <sinch-title type='s' level={'4'} text={t('viaMobile')} />
          </div>

          <sinch-text type='m'>{t('scanQRText')}</sinch-text>
          {imgDataUrl ? (
            <QRCodeImg src={imgDataUrl} $apiError={!channel} />
          ) : (
            <QRCodeImg src={fakeImg} $apiError={true} />
          )}

          <Separator className='separator'>OR</Separator>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <sinch-icon icons-version='2' name='laptop' slot='icon' />
            <sinch-title type='s' level={'4'} text={t('onDeviceHeader')} />
          </div>

          <sinch-text type='m'>{t('onDeviceText', { channel: channelName })}</sinch-text>
          <ActionButtonWrapper>
            <sinch-button
              style={{ minWidth: '100%' }}
              aria-label={t('buttonText', { channel: channelName })}
              text={t('buttonText', { channel: displayName })}
              type={'primary'}
              onClick={() => {
                if (redirectLink) {
                  window.open(redirectLink, '_blank', 'noopener,noreferrer')
                  trackEvent('engage_widget_messaging_channel_clicked', {
                    channel: channel,
                  })
                } else {
                  Sentry.captureMessage(`Forwarding page: unsupported channel "${channel}"`, {
                    level: 'error',
                    extra: { channel, widgetId: widget?.id },
                  })
                }
              }}
              disabled={!channel}
            />
          </ActionButtonWrapper>
        </ContentSection>
      </CenteredContentWrapper>
    </Root>
  )
}
